import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Upload, Loader2, Check, Image, RefreshCw } from 'lucide-react';

interface Asset {
  url: string;
  filename: string;
  modified: number;
}

interface Props {
  /** Currently selected URL, highlighted in the grid. */
  currentUrl?: string;
  /** Called with the chosen URL when the user clicks "Use Image". */
  onSelect: (url: string) => void;
  /** Called when the modal is dismissed without a selection. */
  onClose: () => void;
}

const MediaLibraryModal: React.FC<Props> = ({ currentUrl, onSelect, onClose }) => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [selected, setSelected] = useState<string>(currentUrl || '');
  const fileRef = useRef<HTMLInputElement>(null);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch('/api/brand-guides/assets', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAssets(data.assets || []);
      }
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('access_token');
      const res = await fetch('/api/brand-guides/upload-asset', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await res.json();
      await fetchAssets();
      setSelected(data.url);
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    // Synthesise a change event
    const dt = new DataTransfer();
    dt.items.add(file);
    if (fileRef.current) {
      fileRef.current.files = dt.files;
      fileRef.current.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15,23,42,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: 12, boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
        width: '100%', maxWidth: 860, maxHeight: '85vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #e2e8f0', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Image size={18} style={{ color: '#2177FF' }} />
            <span style={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>Media Library</span>
            <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{assets.length} asset{assets.length !== 1 ? 's' : ''}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={fetchAssets}
              title="Refresh"
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: '#94a3b8', display: 'flex' }}
            >
              <RefreshCw size={15} />
            </button>
            <button
              onClick={onClose}
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: '#64748b', display: 'flex' }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Upload strip */}
        <div style={{
          padding: '12px 20px', borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
          background: '#f8fafc',
        }}>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 8,
              background: '#2177FF', color: '#fff', border: 'none',
              cursor: uploading ? 'default' : 'pointer', fontWeight: 600,
              fontSize: '0.8125rem', opacity: uploading ? 0.7 : 1,
            }}
          >
            {uploading
              ? <><Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Uploading…</>
              : <><Upload size={15} /> Upload New</>
            }
          </button>
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            PNG, JPG, WebP, SVG, GIF · max 10 MB
          </span>
          {uploadError && (
            <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>{uploadError}</span>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            style={{ display: 'none' }}
            onChange={handleUpload}
          />
        </div>

        {/* Asset grid — scrollable */}
        <div
          style={{ flex: 1, overflowY: 'auto', padding: 20 }}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
        >
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#94a3b8', gap: 8 }}>
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
            </div>
          ) : assets.length === 0 ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              height: 200, color: '#94a3b8', gap: 12, border: '2px dashed #e2e8f0', borderRadius: 12,
            }}>
              <Image size={40} style={{ opacity: 0.4 }} />
              <span style={{ fontSize: '0.875rem' }}>No assets yet — upload your first image above</span>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 12,
            }}>
              {assets.map(asset => {
                const isSelected = asset.url === selected;
                return (
                  <button
                    key={asset.url}
                    onClick={() => setSelected(asset.url)}
                    title={asset.filename}
                    style={{
                      position: 'relative', padding: 0, border: 'none',
                      borderRadius: 8, cursor: 'pointer', overflow: 'hidden',
                      outline: isSelected ? '3px solid #2177FF' : '2px solid transparent',
                      outlineOffset: 2,
                      background: '#f1f5f9',
                      aspectRatio: '1',
                      transition: 'outline-color 0.12s',
                    }}
                  >
                    <img
                      src={asset.url}
                      alt={asset.filename}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={e => {
                        const el = e.currentTarget;
                        el.style.display = 'none';
                        (el.nextSibling as HTMLElement | null)?.removeAttribute('hidden');
                      }}
                    />
                    {/* Fallback for SVG / broken images */}
                    <div hidden style={{
                      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', color: '#94a3b8',
                    }}>
                      <Image size={28} />
                    </div>
                    {isSelected && (
                      <div style={{
                        position: 'absolute', top: 6, right: 6,
                        background: '#2177FF', borderRadius: '50%',
                        width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Check size={13} color="#fff" strokeWidth={3} />
                      </div>
                    )}
                    <div style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.55))',
                      padding: '16px 6px 5px',
                    }}>
                      <span style={{
                        fontSize: '0.65rem', color: '#fff', display: 'block',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {asset.filename.replace(/^\d{14}_/, '')}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 18px', borderRadius: 8, border: '1px solid #e2e8f0',
              background: '#fff', cursor: 'pointer', fontSize: '0.875rem', color: '#475569', fontWeight: 600,
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => { if (selected) { onSelect(selected); onClose(); } }}
            disabled={!selected}
            style={{
              padding: '8px 20px', borderRadius: 8, border: 'none',
              background: selected ? '#2177FF' : '#e2e8f0',
              color: selected ? '#fff' : '#94a3b8',
              cursor: selected ? 'pointer' : 'default',
              fontSize: '0.875rem', fontWeight: 700,
              transition: 'background 0.15s',
            }}
          >
            Use Image
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaLibraryModal;
